import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import React, { Fragment } from 'react';

import useDocumentCard from '../../hooks/useDocumentCard';
import getRatio from '../../lib/getRatio';
import normalizeCardTheme from '../../lib/normalizeCardTheme';

import BasicButton from '../buttons/BasicButton';
import LocationsCountIcon from '../icons/LocationCountIcon';
import Category from '../partials/Category';
import DocumentCardMeta from '../partials/DocumentCardMeta';
import DocumentCardThumbnail from '../partials/DocumentCardThumbnail';
import Surtitle from '../partials/Surtitle';
import CardDescription from '../typography/CardDescription';
import CardTitle from '../typography/CardTitle';
import HybridCard from './HybridCard';

import styles from '../../styles/cards/document-hybrid-card.module.css';

interface DocumentHybridCardProps extends Partial<DocumentSnippet> {
    linkQuery?: Record<string, unknown> | null;
    theme?: CardTheme | LegacyCardTheme | null;
    size?: CardSize;
    ratio?: CardRatio;
    height?: number | null;
    placeholder?: boolean;
    imageLoading?: Loading;
    withBorder?: boolean;
    withLocations?: boolean;
    withoutAuthor?: boolean;
    withAuthorImage?: boolean;
    withCategory?: boolean;
    withDescription?: boolean;
    withoutSurtitle?: boolean;
    withoutDuration?: boolean;
    withoutTime?: boolean;
    withBrandIcon?: boolean;
    withoutTypeIcon?: boolean;
    withoutSerie?: boolean;
    className?: string | null;
    onClick?: (() => void) | null;
}

function DocumentHybridCard({
    type = null,
    brands = null,
    locations = null,
    slug = null,
    linkQuery = null,
    title = null,
    surtitle = null,
    subtitle = null,
    image = null,
    coverImage = null,
    categories = null,
    credits = null,
    authors = null,
    sponsors = null,
    duration = null,
    date = null,
    topic = null,
    format = null,
    topics = null,
    formats = null,
    collection = null,
    season = null,
    episode = null,
    micromagNumber = null,
    theme: providedTheme = null,
    size = null,
    ratio = null,
    placeholder = false,
    imageLoading = undefined,
    withBorder = false,
    withLocations = false,
    withoutAuthor = false,
    withAuthorImage = false,
    withCategory = false,
    withDescription = false,
    withoutSurtitle = false,
    withoutDuration = false,
    withoutTime = false,
    withBrandIcon = false,
    withoutTypeIcon = false,
    withoutSerie = false,
    className = null,
    onClick = null,
}: DocumentHybridCardProps) {
    const theme = normalizeCardTheme(providedTheme);
    const url = useUrlGenerator();
    const {
        href,
        category,
        author,
        brandHandle,
        sponsor,
        surtitle: finalSurtitle,
    } = useDocumentCard(
        {
            type,
            slug,
            surtitle,
            brands,
            categories,
            credits,
            authors,
            sponsors,
            topic,
            format,
            topics,
            formats,
            collection,
            season,
            episode,
            micromagNumber,
            date,
        },
        {
            linkQuery,
            placeholder,
            surtitle: {
                withoutSerie,
                withCategory: !withCategory,
            },
        },
    );
    const finalDescription = subtitle;

    const isMenu = theme === 'menu';
    const isMicromag = type === 'micromag';
    const isTiny = size === 'tiny';
    const isBox = theme !== null && theme.indexOf('box') === 0;

    let defaultRatio = 1;
    if (theme === 'menu' || type === 'article') {
        defaultRatio = 5 / 4;
    } else if (type === 'video') {
        defaultRatio = 4 / 5;
    } else if (type === 'micromag') {
        defaultRatio = 4 / 6;
    }

    const thumbnailRatio = getRatio(ratio as any, defaultRatio);

    const showCategory = withCategory && !isTiny;
    const showDescription = withDescription && !isTiny;
    const showSurtitle = !withoutSurtitle && !isTiny;
    const Wrapper: React.ElementType = withLocations ? 'div' : Fragment;

    return (
        <Wrapper
            {...(withLocations
                ? {
                      className: classNames([styles.wrapper, className]),
                  }
                : null)}
        >
            <HybridCard
                href={href || '#'}
                onClick={!placeholder ? onClick : null}
                thumbnail={
                    <DocumentCardThumbnail
                        type={type}
                        image={image}
                        coverImage={coverImage}
                        ratio={ratio}
                        duration={duration}
                        withoutDuration={withoutDuration || isTiny}
                        withDurationInImage
                        withIcon={!placeholder && !isTiny}
                        withBrandIcon={withBrandIcon}
                        withoutTypeIcon={withoutTypeIcon}
                        theme={theme}
                        brand={brandHandle}
                        size="medium"
                        withoutSize
                        withPlaceholder={placeholder}
                        className={classNames([
                            styles.thumbnail,
                            {
                                [styles.gradiant]: isMenu && isMicromag,
                            },
                        ])}
                        loading={imageLoading}
                        iconClassName={styles.thumbnailIcon}
                        pictureClassName={classNames({
                            [styles.containImage]:
                                isMenu && (type === 'micromag' || type === 'podcast'),
                        })}
                        durationClassName={styles.duration}
                    />
                }
                thumbnailRatio={thumbnailRatio}
                className={classNames([
                    styles.container,
                    {
                        [styles.box]: isBox,
                        [styles.hasSponsor]: sponsor !== null,
                        [styles.hasSurtitle]: finalSurtitle !== null && showSurtitle,
                        [styles.withBorder]: withBorder && !isBox,
                    },
                    className,
                ])}
                theme={theme || 'none'}
                size={size || 'default'}
                innerClassName={styles.inner}
                labelClassName={styles.label}
            >
                {(category !== null || placeholder) && showCategory ? (
                    <Category {...category} placeholder={placeholder} className={styles.category} />
                ) : null}

                {finalSurtitle !== null && showSurtitle ? (
                    <Surtitle surtitle={finalSurtitle} className={styles.surtitle} />
                ) : null}

                <CardTitle className={styles.title} placeholder={placeholder} placeholderLines={3}>
                    {title}
                </CardTitle>

                {finalDescription !== null && showDescription ? (
                    <CardDescription
                        className={styles.description}
                        placeholder={placeholder}
                        placeholderLines={3}
                    >
                        {finalDescription}
                    </CardDescription>
                ) : null}

                <DocumentCardMeta
                    tag="span"
                    author={type !== 'micromag' ? author : null}
                    sponsor={sponsor}
                    date={date}
                    placeholder={placeholder}
                    withoutAuthor={withoutAuthor}
                    withoutAuthorImage={!withAuthorImage}
                    withoutTime={withoutTime}
                    className={styles.meta}
                    authorClassName={styles.author}
                    sponsorClassName={styles.sponsor}
                    timeClassName={styles.time}
                />
            </HybridCard>
            {withLocations && locations !== null && locations.length > 0 ? (
                <BasicButton
                    href={
                        slug !== null
                            ? url('map.document', {
                                  document: slug,
                              })
                            : null
                    }
                    className={classNames([
                        styles.container,
                        styles.locations,
                        {
                            [styles.box]: isBox,
                            [styles.hasSponsor]: sponsor !== null,
                            [styles.withBorder]: withBorder,
                        },
                        className,
                    ])}
                    data-card-presentation="hybrid"
                    data-card-theme={theme || 'none'}
                    data-card-size={size || 'default'}
                >
                    <LocationsCountIcon
                        iconCircleColor="transparent"
                        count={locations.length}
                        countClassName={styles.locationCount}
                        className={styles.locationIcon}
                    />
                </BasicButton>
            ) : null}
        </Wrapper>
    );
}

export default DocumentHybridCard;
